import {Component, HostBinding, OnDestroy, OnInit} from '@angular/core';
import {SplashScreenService} from './core/service/splash-screen.service';
import {OverlayContainer} from '@angular/cdk/overlay';
import {ThemeService} from '@app/core/service/theme.service';
import {environment} from '@env/environment';
import {select, Store} from '@ngrx/store';
import {getAuthenticatedUser, State} from '@app/app.reducers';
import {filter, map, take, mergeMap} from 'rxjs/operators';
import {User} from '@app/core/model/user';
import {Subscription} from 'rxjs';
import {AuthenticatedUserService} from '@app/core/service/authenticated-user.service';
import {ActivatedRoute, NavigationEnd, Router, RouterEvent, Scroll} from '@angular/router';
import {AuthenticationService} from '@app/core/service/authentication.service';
import {MixpanelService} from '@app/core/service/mixpanel.service';
import {PageTitleService} from '@app/core/service/pageTitle.service';

const RoutesWithCodeParam = [
    '/admin/applications/coursera/callback',
    '/admin/applications/google-classroom/callback',
    '/admin/applications/gsuite/callback'
];

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {

    private subscriptions: Subscription[] = [];
    private pageTitleChangedSubscription: Subscription;

    @HostBinding('class') activeThemeCssClass: string;

    constructor(
        private authenticatedUserService: AuthenticatedUserService,
        private authenticationService: AuthenticationService,
        private splashScreen: SplashScreenService,
        private themeService: ThemeService,
        private overlayContainer: OverlayContainer,
        private store: Store<State>,
        private router: Router,
        private mixpanelService: MixpanelService,
        private pageTitleService: PageTitleService,
        private activatedRoute: ActivatedRoute
    ) {
        this.setThemeClass(themeService.getActiveThemeName() + '-theme');
    }

    setThemeClass(theme: string) {
        const cssClass = theme;

        const classList: DOMTokenList = this.overlayContainer.getContainerElement().classList;
        if (classList.contains(this.activeThemeCssClass)) {
            classList.remove(this.activeThemeCssClass);
            classList.add(cssClass);
        } else {
            classList.add(cssClass);
        }

        this.activeThemeCssClass = cssClass;
    }

    ngOnInit() {
        if (environment.stsServer) {
            this.subscriptions.push(
                this.router.events.pipe(
                    filter((event) => event instanceof NavigationEnd),
                    map((event: RouterEvent) => (event instanceof Scroll) ? event.routerEvent : event),
                    take(1)
                ).subscribe((event: NavigationEnd) => {
                    if (event.url && RoutesWithCodeParam.some((routePath: string) => event.url.startsWith(routePath))) {
                        // Do not check auth for RoutesWithCodeParam routes as angular-auth-oidc-client intercept the execution
                        return;
                    } else {
                        if (!event.url || (event.url.indexOf('/login/district') !== 0)) {
                            this.authenticationService.checkETXAuth();
                        }
                    }
                })
            );
        }

        this.initAnalytics();
    }

    private hideSplashScreen() {
        this.splashScreen.setHideOnNavigationEnd(true);
        this.splashScreen.hide();
    }

    private initAnalytics() {
        this.subscriptions.push(
            this.store.pipe(
                select(getAuthenticatedUser),
                take(1),
                filter((user: User) => !!user)
            ).subscribe((user: User) =>
                this.mixpanelService.identify(user)
            )
        );

        this.subscriptions.push(
            this.router.events.pipe(
                filter(event => (event instanceof NavigationEnd)),
                map(() => this.activatedRoute),
                map(route => {
                    while (route.firstChild) {
                        route = route.firstChild;
                    }
                    return route;
                }),
                filter(route => route.outlet === 'primary'),
                mergeMap(route => route.data)
            ).subscribe((data) => {
                if (this.pageTitleChangedSubscription) {
                    this.pageTitleChangedSubscription.unsubscribe();
                }

                if (data.trackPageview === true || data.trackPageview === undefined) {
                    this.pageTitleChangedSubscription = this.pageTitleService.pageTitle$
                        .pipe(take(1))
                        .subscribe((pageTitle) =>
                            this.mixpanelService.trackPageview(pageTitle, this.router.url)
                        );
                }
            })
        );
    }

    ngOnDestroy() {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
        if (this.pageTitleChangedSubscription) {
            this.pageTitleChangedSubscription.unsubscribe();
        }
    }
}
